/**
 * Estado de la tarea como píldora en la barra superior de la ficha. Es un `<select>` nativo con aspecto de
 * píldora (así funciona con teclado y lectores de pantalla) y mueve la tarea con el mismo movimiento
 * optimista del tablero (`useMoverTarea`). Sin permiso de escritura se muestra como texto.
 *
 * Ofrece los estados del flujo y, al final, «Descartada…»: elegirla no mueve nada todavía, abre
 * `DialogoDescartar` para pedir el motivo. Cancelar (o Esc) devuelve el estado anterior y el foco al select.
 *
 * Cerrar (Hecho o Descartada) una madre con subtareas abiertas o una tarea que otras abiertas esperan, o llevar
 * una tarea bloqueada a En curso, En revisión o Hecho, pide antes confirmación (`useConfirmarCierre`, un solo
 * diálogo aunque pasen varias cosas); al descartar, primero se confirma y luego se pide el motivo.
 */
import { useCallback, useRef, useState } from 'react';
import {
  ESTADOS_DEL_FLUJO,
  ETIQUETA_ESTADO,
  type DescarteEntrada,
  type Estado,
} from '@cdtec-backlog/contrato';
import { mensajeDeError } from '../../lib/api';
import { useConfirmarCierre, type TareaAMover } from './ConfirmarCierre';
import { DialogoDescartar } from './DialogoDescartar';
import { useMoverTarea } from './hooks';
import './SelectorEstado.css';

interface Props {
  /** La tarea: id, número (CDT-N, para los diálogos), estado, avance de sus subtareas y bloqueantes. */
  tarea: TareaAMover;
  puedeEscribir: boolean;
}

export function SelectorEstado({ tarea, puedeEscribir }: Props) {
  const { id: tareaId, numero, estado } = tarea;
  const mover = useMoverTarea();
  // El descarte muestra su error dentro del diálogo, no como aviso flotante.
  const descartar = useMoverTarea({ errorEnLinea: true });
  const [descartando, setDescartando] = useState(false);
  const { confirmarCierre, dialogo: confirmacion, pidiendo } = useConfirmarCierre();
  const select = useRef<HTMLSelectElement>(null);

  const cancelar = useCallback(() => {
    setDescartando(false);
    descartar.reset();
    select.current?.focus();
  }, [descartar]);

  if (!puedeEscribir)
    return <span className="estado estado--lectura">{ETIQUETA_ESTADO[estado]}</span>;

  // Mientras se confirma o se pide el motivo, el select ya muestra el estado elegido.
  const mostrado: Estado = descartando
    ? 'DESCARTADA'
    : (pidiendo ?? (mover.isPending ? mover.variables.estado : estado));

  const confirmar = (descarte: DescarteEntrada) =>
    descartar.mutate(
      { id: tareaId, estado: 'DESCARTADA', descarte },
      {
        onSuccess: () => {
          setDescartando(false);
          select.current?.focus();
        },
      },
    );

  return (
    <span className={`estado${mostrado === 'DESCARTADA' ? ' estado--descartada' : ''}`}>
      <select
        ref={select}
        aria-label="Estado"
        value={mostrado}
        disabled={mover.isPending || descartar.isPending}
        onChange={(e) => {
          const nuevo = e.target.value as Estado;
          confirmarCierre(
            tarea,
            nuevo,
            () =>
              nuevo === 'DESCARTADA'
                ? setDescartando(true)
                : mover.mutate({ id: tareaId, estado: nuevo }),
            () => select.current?.focus(),
          );
        }}
      >
        {ESTADOS_DEL_FLUJO.map((e) => (
          <option key={e} value={e}>
            {ETIQUETA_ESTADO[e]}
          </option>
        ))}
        {/* Ya descartada se lee «Descartada»; si no, los puntos avisan que pide algo más. */}
        <option value="DESCARTADA">{estado === 'DESCARTADA' ? 'Descartada' : 'Descartada…'}</option>
      </select>
      {confirmacion}
      {descartando && (
        <DialogoDescartar
          numero={numero}
          enviando={descartar.isPending}
          errorApi={descartar.isError ? mensajeDeError(descartar.error) : null}
          onCancelar={cancelar}
          onConfirmar={confirmar}
        />
      )}
    </span>
  );
}
